// Shared helpers for the Historical Cases, Court Services (CTC) and Media Instruments pages.
import * as Constants from "../../constants";

export const JIS_START = "2013-10-02";

// fetch JSON from the API; returns the parsed body, or { code: -999 } on network errors
export async function api(token, setIsExpired, path, { method = "GET", body } = {}) {
  try {
    const resp = await fetch(`${Constants.apiUrl}/${path}`, {
      method,
      body: body === undefined ? undefined : JSON.stringify(body),
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    let data = {};
    try {
      data = await resp.json();
    } catch (e) {
      data = { code: resp.ok ? 0 : -999, status: resp.ok ? "" : "The server returned an error" };
    }
    if (data.code === -10 && setIsExpired) setIsExpired(true);
    return data;
  } catch (e) {
    console.error(e);
    return { code: -999, status: Constants.serverErr || "Unable to reach the server" };
  }
}

// download a file returned by the API (POST or GET), keeping errors readable
export async function download(token, setIsExpired, path, filename, { method = "GET", body } = {}) {
  const resp = await fetch(`${Constants.apiUrl}/${path}`, {
    method,
    body: body === undefined ? undefined : JSON.stringify(body),
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
  });
  if (resp.status === 409 || resp.headers.get("content-type")?.includes("application/json")) {
    const d = await resp.json().catch(() => ({}));
    if (d.code === -10 && setIsExpired) setIsExpired(true);
    throw new Error(d.status || "Download failed");
  }
  if (!resp.ok) throw new Error("Download failed");
  const blob = await resp.blob();
  const href = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(href);
}

// returns a blob URL (for previews of generated PDFs)
export async function blobUrl(token, path, body) {
  const resp = await fetch(`${Constants.apiUrl}/${path}`, {
    method: "POST",
    body: JSON.stringify(body || {}),
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
  });
  if (resp.headers.get("content-type")?.includes("application/json")) {
    const d = await resp.json().catch(() => ({}));
    throw new Error(d.status || "Preview failed");
  }
  const blob = await resp.blob();
  return window.URL.createObjectURL(blob);
}

export const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const s = String(reader.result || "");
      resolve(s.substring(s.indexOf(",") + 1));
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export const docLink = (url) => (url ? Constants.docUrl + String(url).replace(/^\/+/, "") : "");

export const fmtDate = (d, f = "DD MMM YYYY") => (d ? Constants.momentDate(d).format(f) : "");
export const fmtDateTime = (d) => fmtDate(d, "DD MMM YYYY, hh:mm a");
export const toInputDate = (d) => (d ? Constants.momentDate(d).format("YYYY-MM-DD") : "");
export const money = (v) => `N ${Constants.formatCurrency(v || 0)}`;

export const classifyByDate = (d) => {
  if (!d) return "Unknown";
  return d < JIS_START ? "Historical" : "JIS";
};

// colour for a status / label pill
const TONES = {
  good: ["Verified", "Fully Digitized", "Available Digitally", "Active", "Approved", "Completed", "Ready For Download", "Payment Successful", "JIS", "Imported", "Ready"],
  warn: [
    "Pending Verification", "Partially Verified", "Partially Digitized", "Partially Available", "Pending Retrieval", "Pending Digitization",
    "Awaiting Payment", "Pending Assessment", "Under ACR Review", "Historical Case Verification Required", "Historical Case Under Registry Review",
    "Pending Archive Retrieval", "Under Review", "Unverified", "Pending", "Pending Judge Approval", "Under Certification", "Duplicate",
  ],
  bad: ["Rejected", "Rework Required", "Rework Requested", "Record Not Found", "Not Found", "Destroyed or Missing", "Expired", "Suspended", "Error"],
  info: ["Historical", "Linked Historical/JIS", "Linked", "Merged", "Assigned To Registrar", "Assigned To Archive", "Assigned To Deputy Sheriff", "Submitted"],
};
export const tone = (s) => Object.keys(TONES).find((k) => TONES[k].includes(s)) || "plain";

export const reqTypeTitle = (config, code) => config?.requestTypes?.find((r) => r.code === code)?.title || code;

export const firstError = (data, fallback = "Something went wrong") => data?.status || fallback;
